import React, { useState, useEffect, useRef } from "react";

const AnimatedNumber = ({
  number = 750,
  animationDuration = 1000,
  ...props
}) => {
  const [counts, setCounts] = useState([]);
  const [isVisible, setIsVisible] = useState(false);
  const componentRef = useRef(null);
  const animationRef = useRef(null);
  const digits = String(number).split("").map(Number);

  useEffect(() => {
    // Create intersection observer
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          setIsVisible(true);
        } else {
          // Reset when out of view
          setIsVisible(false);
          setCounts(digits.map(() => 0));
          if (animationRef.current) {
            animationRef.current.forEach((interval) => clearInterval(interval));
          }
        }
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: 0.1, // Trigger when at least 10% is visible
      }
    );

    if (componentRef.current) {
      observer.observe(componentRef.current);
    }

    return () => {
      if (componentRef.current) {
        observer.unobserve(componentRef.current);
      }
    };
  }, [digits]);

  useEffect(() => {
    if (!isVisible) return;

    // Reset counts
    setCounts(digits.map(() => 0));

    // Store all interval IDs for cleanup
    animationRef.current = [];

    // Animate each digit
    digits.forEach((targetNum, index) => {
      if (targetNum === 0) return; // Skip animation for 0

      const steps = targetNum;
      const stepDuration = animationDuration / steps;

      let current = 0;
      const interval = setInterval(() => {
        if (current < targetNum) {
          current += 1;
          setCounts((prev) => {
            const newCounts = [...prev];
            newCounts[index] = current;
            return newCounts;
          });
        } else {
          clearInterval(interval);
        }
      }, stepDuration);

      animationRef.current.push(interval);
    });

    return () => {
      if (animationRef.current) {
        animationRef.current.forEach((interval) => clearInterval(interval));
      }
    };
  }, [isVisible, number, animationDuration]);

  return (
    <div ref={componentRef} {...props}>
      {counts.map((count, index) => (
        <div key={index}>{count}</div>
      ))}
    </div>
  );
};

export default AnimatedNumber;
